// The bar reveals only when scrolled back to (essentially) the very top, and
// hides once scrolled past HIDE_THRESHOLD. The gap between the two is a
// hysteresis dead zone where the open state is left untouched.
//
// This gap MUST stay wider than the bar's own height (~42px, see
// blocks/_pencil-bar.liquid `min-h-[42px]`). The bar sits in normal flow inside
// the sticky header, so opening/closing it changes document height at the top
// and scroll anchoring shifts `scrollY` by that amount. With a single
// threshold that shift would carry `scrollY` back across the line, flipping the
// state and making the bar flicker open/closed at certain heights near the top.
// The dead zone absorbs that shift so it can never reach the opposite edge.
const REVEAL_THRESHOLD = 8; // scrollY <= this: reveal
const HIDE_THRESHOLD = 90; // scrollY >  this: hide

/**
 * Pencil bar (`sections/pencil-bar.liquid`): announcement bar that
 *  - stays dismissed (keyed by `pencilBarId`) once the visitor closes it, via a
 *    long-lived cookie, and
 *  - is open by default and slides closed when the page is scrolled away from
 *    the top, sliding back open when scrolled to the top again.
 *
 * Drives `open`; the slide-toggle-container body mirrors it onto `slideOpen`
 * (accordion-style) to run the actual slide animation.
 *
 * @param {object} opts
 * @param {string} opts.pencilBarId    Dismissal cookie key.
 * @param {number} opts.autoplayDelay  Swiper autoplay delay (ms).
 */
export default function pencilBar({ pencilBarId, autoplayDelay = 5000 } = {}) {
  return {
    open: true, // open by default
    dismissed: false,
    pencilBarId,
    onScroll: null,
    swiperEl: null,

    cookieMatch() {
      const match = document.cookie.match(/(?:^|;\s*)hide_pencil_bar_id=([^;]+)/);
      return match && decodeURIComponent(match[1]) === this.pencilBarId;
    },

    // Mirror visibility onto <html> so the header can go flush + square-cornered
    // whenever the pencil bar can't be seen (see modules/shyHeader.js).
    syncVisibility(visible) {
      document.documentElement.classList.toggle('js-pencil-visible', visible);
    },

    init() {
      this.$watch('open', (value) => {
        this.syncVisibility(value);
        this.toggleSwiper(value);
      });

      // Permanently dismissed: leave the wrapper hidden and never wire anything up.
      if (this.cookieMatch()) {
        this.dismissed = true;
        this.open = false;
        this.syncVisibility(false);
        return;
      }

      this.$el.classList.remove('hidden');
      this.initSwiper();

      this.onScroll = () => {
        if (this.dismissed) return;
        const y = window.scrollY;
        if (y <= REVEAL_THRESHOLD) this.open = true;
        else if (y > HIDE_THRESHOLD) this.open = false;
        // Between the two thresholds we keep the current state, so the bar's
        // own height change can't trigger an inverse toggle (see above).
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll(); // set initial state for mid-page loads/refreshes
      this.syncVisibility(this.open);
    },

    destroy() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      document.documentElement.classList.remove('js-pencil-visible');
    },

    // Fade-effect Swiper computes slide opacity from slide geometry. While the
    // bar is collapsed (`display:none`), autoplay keeps firing fades against
    // zero geometry and leaves the active slide stuck at opacity 0 — which is
    // why only a resize (forcing a recompute) brought it back. So we pause
    // autoplay while hidden and update + resume on reveal.
    toggleSwiper(visible) {
      const swiper = this.swiperEl?.swiper;
      if (!swiper?.autoplay) return;
      if (visible) {
        requestAnimationFrame(() => {
          swiper.update();
          swiper.autoplay.start();
        });
      } else {
        swiper.autoplay.stop();
      }
    },

    initSwiper() {
      this.swiperEl = this.$el.querySelector('swiper-container');
      const sw = this.swiperEl;
      if (!sw) return;
      Object.assign(sw, {
        slidesPerView: 1,
        loop: true,
        grabCursor: true,
        effect: 'fade',
        fadeEffect: { crossFade: true },
        autoplay: {
          delay: autoplayDelay,
          disableOnInteraction: false,
        },
      });
      sw.initialize();
    },

    setCookieandClose() {
      document.cookie = `hide_pencil_bar_id=${encodeURIComponent(this.pencilBarId)}; path=/; max-age=31536000; SameSite=Lax`;
      this.dismissed = true;
      this.open = false;
    },
  };
}
